import { compact, sortBy } from 'lodash'
import { Fragment } from 'react'

import { Field } from '../../models/Field'
import { PSRecord } from '../../models/PSRecord'
import { Type } from '../../models/Type'
import { User } from '../../models/User'
import { API_BASE } from '../../utils/constants'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { RecordStatusChecklist } from '../common/RecordStatusChecklist'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { CustomFieldView } from './CustomFieldView'

type Props = {
    form: PSRecord
    client: User
    type: Type
    customFields: Field[]
}

export const RecordView = ({ form, client, type, customFields }: Props) => {
    const fields = compact(
        sortBy(customFields, (field) => field.seqnum ?? Infinity).map((field) => {
            const value = form.fieldValues[field.name ?? '']
            if (value === undefined) return null

            return {
                id: field.id ?? '',
                title: field.label ?? '',
                field,
                value,
            }
        })
    )

    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center">
                        <Icon
                            icon="field.large"
                            background="round"
                            className="size-[116px]"
                            svgClassName="size-[56px]"
                        />

                        <Heading size="sm" className="mt-6 mb-1 text-center">
                            {form.type?.name}
                        </Heading>

                        <Paragraph size="lg" className="mb-1 font-normal text-gray-600">
                            {type.refnum}
                        </Paragraph>
                    </div>

                    <Divider className="my-6" />

                    <div className="flex flex-col items-stretch justify-start gap-y-6">
                        {!!form.subaccount && (
                            <BaseRecordView.IconField
                                icon={
                                    <ContactAvatar
                                        user={form.subaccount}
                                        containerClassName="row-span-2"
                                        hasHoverEffect
                                    />
                                }
                                title={form.subaccount.name ?? ''}
                                subtitle={form.subaccount.labelString}
                                href={`/subaccounts/${form.subaccount.id}`}
                            />
                        )}

                        <BaseRecordView.IconField
                            icon={<ContactAvatar user={client} hasHoverEffect />}
                            title={client.name ?? ''}
                            subtitle="Client"
                            secondaryView={
                                <a href={`/messages/${client.id}`}>
                                    <Button intent="secondary" size="small">
                                        Chat
                                    </Button>
                                </a>
                            }
                            href={`/clients/${client.id}`}
                        />
                    </div>
                </Card>

                <Card size="sm" title="Status">
                    <RecordStatusChecklist
                        items={form.statuses.map((status) => ({
                            title: status.title,
                            subtitle: status.date?.format('MMM Do, YYYY [at] h:mm A'),
                            status: status.date !== undefined ? 'checked' : 'empty',
                        }))}
                        className="mt-6"
                    />
                </Card>
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                <Card title="Fields">
                    <div className="mt-8 flex flex-col items-stretch justify-start">
                        {fields.map(({ id, title, value, field }, fieldIndex) => (
                            <Fragment key={id}>
                                <div className="flex flex-col items-stretch justify-center gap-y-2">
                                    <Label size="lg">{title}</Label>
                                    <Paragraph size="md" className="text-gray-600">
                                        <CustomFieldView field={field} value={value} />
                                    </Paragraph>
                                </div>

                                {fieldIndex < fields.length - 1 && <Divider className="my-4" />}
                            </Fragment>
                        ))}
                    </div>
                </Card>

                {form.signature !== undefined && (
                    <Card title="Contract">
                        <button
                            onClick={() =>
                                window.open(
                                    `${API_BASE}/signature/${form.signature!.id}/print`,
                                    '_blank'
                                )
                            }
                            className="mt-8 flex w-full items-center justify-center gap-x-4 rounded-lg border border-gray-300 bg-gray-100 px-4 py-3 text-left hover:bg-gray-300"
                        >
                            <Icon
                                icon="file.large"
                                background="round"
                                backgroundColorClassName="bg-gray-600"
                                className="text-white"
                                shouldInheritColor
                            />
                            <div className="shrink grow">
                                <Label size="sm" className="text-gray-700">
                                    Signature
                                </Label>
                                <Paragraph size="sm" className="text-gray-600">
                                    PDF
                                </Paragraph>
                            </div>
                            <Icon icon="chevronRight.medium" className="text-gray-600" />
                        </button>
                    </Card>
                )}
            </BaseRecordView.RightColumn>
        </BaseRecordView.Container>
    )
}
